import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { PageHeader, Panel, StatusBadge, PageLoader, BRAND_ACCENTS, Btn } from "@/lib/ui";
import { Building2, ArrowUpRight } from "lucide-react";

export default function Brands() {
  const api = useApi();
  const { brand, setBrand } = useBrand();
  const { data: brands = [], isLoading } = useQuery({ queryKey: ["brands"], queryFn: () => api.get("/brands") });

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader kicker="Group Architecture" title="Brand Workspaces" />
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        The Command Centre sits above all five brands. Each brand is now a live workspace with the
        full feature set — agents, approvals, payments, tasks, documents and media — scoped to that
        brand. Switch brands from the top bar or enter a workspace below.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {brands.map((b) => {
          const accent = BRAND_ACCENTS[b.code] || b.accent;
          const active = b.state === "ACTIVE";
          return (
            <Panel key={b.code} className="p-5 relative overflow-hidden" data-testid={`brand-${b.code}`}>
              <div className="absolute top-0 left-0 h-full w-1" style={{ background: `hsl(${accent})` }} />
              <div className="flex items-start justify-between pl-2">
                <div>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" style={{ color: `hsl(${accent})` }} />
                    <span className="font-head text-xl font-bold">{b.name}</span>
                  </div>
                  <div className="text-[11px] font-mono text-muted-foreground mt-1">{b.code}</div>
                  <p className="text-sm text-muted-foreground mt-2 max-w-sm">{b.description}</p>
                </div>
                <StatusBadge tone={active ? "ok" : "muted"} dot={active}>
                  {active ? "Active" : "Coming Soon"}
                </StatusBadge>
              </div>
              <div className="pl-2 mt-4">
                {active ? (
                  <Btn size="sm" variant={brand === b.code ? "primary" : "outline"} onClick={() => setBrand(b.code)} data-testid={`set-brand-${b.code}`}>
                    {brand === b.code ? "Current Workspace" : "Enter Workspace"} <ArrowUpRight className="h-3.5 w-3.5" />
                  </Btn>
                ) : (
                  <span className="text-xs text-muted-foreground font-mono">Workspace shell reserved</span>
                )}
              </div>
            </Panel>
          );
        })}
      </div>
    </div>
  );
}
